import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { FormOfSurrender } from "@/types";
import { getFieldError } from "@/utils";

const MAX_FORM_OF_SURRENDER_ENTRIES = 6;

export function FormOfSurrenderStep() {
  const { register, watch, setValue, formState: { errors } } = useFormContext();
  const formOfSurrenders: FormOfSurrender[] = watch("formOfSurrenders") || [];
  const hasReachedMax = formOfSurrenders.length >= MAX_FORM_OF_SURRENDER_ENTRIES;

  const addEntry = () => {
    if (hasReachedMax) return;

    setValue("formOfSurrenders", [
      ...formOfSurrenders,
      { agreementNumber: "", product: "", week: undefined, points: undefined },
    ]);
  };

  const removeEntry = (index: number) => {
    setValue("formOfSurrenders", formOfSurrenders.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label>Form of Surrender</Label>
        <Button type="button" variant="outline" size="sm" onClick={addEntry} disabled={hasReachedMax}>
          <Plus className="h-4 w-4 mr-1" /> Add Entry
        </Button>
      </div>

      {formOfSurrenders.length === 0 && (
        <p className="text-sm text-muted-foreground">No form of surrender entries added.</p>
      )}

      {hasReachedMax && (
        <p className="text-sm text-muted-foreground">
          Maximum of {MAX_FORM_OF_SURRENDER_ENTRIES} form of surrender entries reached.
        </p>
      )}

      {formOfSurrenders.map((_, index) => {
        const weekValue = watch(`formOfSurrenders.${index}.week`);
        const pointsValue = watch(`formOfSurrenders.${index}.points`);
        const hasWeek = weekValue !== undefined && weekValue !== null && !Number.isNaN(weekValue);
        const hasPoints = pointsValue !== undefined && pointsValue !== null && !Number.isNaN(pointsValue);

        return (
          <div key={index} className="grid grid-cols-5 gap-4 items-end border-b pb-4">
            <div className="space-y-2">
              <Label htmlFor={`formOfSurrenders.${index}.agreementNumber`}>Agreement Number</Label>
              <Input
                id={`formOfSurrenders.${index}.agreementNumber`}
                {...register(`formOfSurrenders.${index}.agreementNumber`, {
                  required: true,
                })}
                className={getFieldError(errors, `formOfSurrenders.${index}.agreementNumber`) ? "border-destructive ring-destructive" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`formOfSurrenders.${index}.product`}>Product</Label>
              <Input
                id={`formOfSurrenders.${index}.product`}
                {...register(`formOfSurrenders.${index}.product`, {
                  required: true,
                })}
                className={getFieldError(errors, `formOfSurrenders.${index}.product`) ? "border-destructive ring-destructive" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`formOfSurrenders.${index}.week`}>Week</Label>
              <Input
                id={`formOfSurrenders.${index}.week`}
                type="number"
                min={1}
                max={52}
                {...register(`formOfSurrenders.${index}.week`, {
                  valueAsNumber: true,
                  disabled: hasPoints,
                  validate: (value) => {
                    if (hasPoints) return true;
                    if (value === undefined || Number.isNaN(value)) return false;
                    if (value < 1 || value > 52) return "Week must be between 1 and 52";

                    return true;
                  },
                })}
                className={getFieldError(errors, `formOfSurrenders.${index}.week`) ? "border-destructive ring-destructive" : ""}
              />
              {typeof getFieldError(errors, `formOfSurrenders.${index}.week`)?.message === "string" &&
                getFieldError(errors, `formOfSurrenders.${index}.week`)?.message && (
                  <p className="text-sm text-destructive mt-1">
                    {getFieldError(errors, `formOfSurrenders.${index}.week`)?.message as string}
                  </p>
                )}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`formOfSurrenders.${index}.points`}>Points</Label>
              <Input
                id={`formOfSurrenders.${index}.points`}
                type="number"
                {...register(`formOfSurrenders.${index}.points`, {
                  valueAsNumber: true,
                  disabled: hasWeek,
                  validate: (value) => hasWeek || (value !== undefined && !Number.isNaN(value)),
                })}
                className={getFieldError(errors, `formOfSurrenders.${index}.points`) ? "border-destructive ring-destructive" : ""}
              />
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => removeEntry(index)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      })}

      <div className="space-y-2">
        <Label htmlFor="notes.formOfSurrenderNotes">Form of Surrender Notes</Label>
        <Textarea
          id="notes.formOfSurrenderNotes"
          {...register("notes.formOfSurrenderNotes")}
          placeholder="Note that applies to the form of surrender as a whole, not a specific entry"
          className="min-h-[120px]"
        />
      </div>
    </div>
  );
}
